import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";

export type DuplicateLegRow = {
  id: string;
  leg_sequence: number | null;
  trip_type: string | null;
  ready_time: string | null;
  ready_time_end: string | null;
  requested_delivery_time: string | null;
  requested_delivery_time_end: string | null;
  ship_from_label: string;
  ship_to_label: string;
  customer_leg_id: string | null;
  tractor_name: string | null;
  operator_names: string[];
};

export type DuplicateOrderRow = {
  id: string;
  unique_id: string | null;
  status: string | null;
  tender_id: string | null;
  is_api_created: boolean | null;
  api_source: string | null;
  billing_party_id: string | null;
  ship_from_location_id: string | null;
  ship_to_location_id: string | null;
  ready_time: string | null;
  ready_time_end: string | null;
  requested_delivery_time: string | null;
  requested_delivery_time_end: string | null;
  trailer_type_id: string | null;
  trip_type: string | null;
  notes: string | null;
  assigned_tractor_id: string | null;
  assigned_operator_id: string | null;
  assigned_operator_2_id: string | null;
  assigned_tractor: { id: string; name: string | null } | null;
  assigned_operator: { id: string; name: string | null } | null;
  assigned_operator_2: { id: string; name: string | null } | null;
  ship_from_location: { id: string; short_code: string | null; name: string | null } | null;
  ship_to_location: { id: string; short_code: string | null; name: string | null } | null;
  billing_party: { id: string; organization_name: string | null; short_code: string | null } | null;
  legs: DuplicateLegRow[];
};

export type DuplicatePair = {
  a: DuplicateOrderRow;
  b: DuplicateOrderRow;
  matchReason: string;
};

const ORDER_SELECT = `
  id, unique_id, status, tender_id, is_api_created, api_source,
  billing_party_id, ship_from_location_id, ship_to_location_id,
  ready_time, ready_time_end, requested_delivery_time, requested_delivery_time_end,
  trailer_type_id, trip_type, notes,
  assigned_tractor_id, assigned_operator_id, assigned_operator_2_id
`;

type DuplicateOrderBaseRow = Omit<
  DuplicateOrderRow,
  | "ship_from_location"
  | "ship_to_location"
  | "assigned_tractor"
  | "assigned_operator"
  | "assigned_operator_2"
  | "legs"
>;
type DuplicateCustomerRow = { id: string; organization_name: string | null; short_code: string | null };
type NamedRow = { id: string; name: string | null };

type DuplicateLocationRow = {
  id: string;
  short_code: string | null;
};

export function useDuplicateOrderPairs() {
  const { tenantId, tenantReady } = useTenant();

  return useQuery({
    queryKey: ["duplicate-order-pairs", tenantId],
    enabled: tenantReady && !!tenantId,
    refetchInterval: 60_000,
    queryFn: async (): Promise<DuplicatePair[]> => {
      const { data: pairsData, error: pairsError } = await supabase.rpc(
        "find_duplicate_order_pairs",
        { _tenant_id: tenantId! },
      );
      if (pairsError) throw pairsError;
      const pairs = (pairsData ?? []) as Array<{
        order_a_id: string;
        order_b_id: string;
        match_reason: string;
      }>;
      if (pairs.length === 0) return [];

      const ids = Array.from(new Set(pairs.flatMap((p) => [p.order_a_id, p.order_b_id])));
      const { data: ordersData, error: ordersError } = await supabase
        .from("orders")
        .select(ORDER_SELECT)
        .in("id", ids)
        .eq("tenant_id", tenantId!);
      if (ordersError) throw ordersError;

      const orders = (ordersData ?? []) as Omit<DuplicateOrderBaseRow, "billing_party">[];

      // Legs for each order (multi-leg breakdown shown on the comparison cards).
      const { data: legsData, error: legsError } = await supabase
        .from("legs")
        .select(
          `id, order_id, leg_sequence, trip_type, ready_time, ready_time_end,
           requested_delivery_time, requested_delivery_time_end, customer_leg_id,
           ship_from_location_id, ship_to_location_id, ship_from_city, ship_to_city,
           assigned_tractor_id, assigned_operator_id, assigned_operator_2_id`,
        )
        .in("order_id", ids)
        .eq("tenant_id", tenantId!)
        .order("leg_sequence", { ascending: true });
      if (legsError) throw legsError;
      const legRows = (legsData ?? []) as Array<{
        id: string;
        order_id: string | null;
        leg_sequence: number | null;
        trip_type: string | null;
        ready_time: string | null;
        ready_time_end: string | null;
        requested_delivery_time: string | null;
        requested_delivery_time_end: string | null;
        customer_leg_id: string | null;
        ship_from_location_id: string | null;
        ship_to_location_id: string | null;
        ship_from_city: string | null;
        ship_to_city: string | null;
        assigned_tractor_id: string | null;
        assigned_operator_id: string | null;
        assigned_operator_2_id: string | null;
      }>;

      const locationIds = Array.from(
        new Set(
          [
            ...orders.flatMap((o) => [o.ship_from_location_id, o.ship_to_location_id]),
            ...legRows.flatMap((l) => [l.ship_from_location_id, l.ship_to_location_id]),
          ]
            .filter((id): id is string => Boolean(id)),
        ),
      );

      const locationById = new Map<string, DuplicateLocationRow>();
      if (locationIds.length > 0) {
        const { data: locationsData, error: locationsError } = await supabase
          .from("locations")
          .select("id, short_code")
          .in("id", locationIds);
        if (locationsError) throw locationsError;
        for (const location of (locationsData ?? []) as DuplicateLocationRow[]) {
          locationById.set(location.id, location);
        }
      }

      const customerIds = Array.from(
        new Set(orders.map((o) => o.billing_party_id).filter((id): id is string => Boolean(id))),
      );
      const customerById = new Map<string, DuplicateCustomerRow>();
      if (customerIds.length > 0) {
        const { data: customersData, error: customersError } = await supabase
          .from("customers")
          .select("id, organization_name, short_code")
          .in("id", customerIds);
        if (customersError) throw customersError;
        for (const customer of (customersData ?? []) as DuplicateCustomerRow[]) {
          customerById.set(customer.id, customer);
        }
      }

      const byId = new Map<string, DuplicateOrderRow>();
      const legLabel = (locId: string | null, city: string | null) =>
        (locId ? locationById.get(locId)?.short_code : null) || city || "—";
      const tractorIds = Array.from(
        new Set(
          [
            ...orders.map((o) => o.assigned_tractor_id),
            ...legRows.map((l) => l.assigned_tractor_id),
          ].filter((id): id is string => Boolean(id)),
        ),
      );
      const driverIds = Array.from(
        new Set(
          [
            ...orders.flatMap((o) => [o.assigned_operator_id, o.assigned_operator_2_id]),
            ...legRows.flatMap((l) => [l.assigned_operator_id, l.assigned_operator_2_id]),
          ].filter((id): id is string => Boolean(id)),
        ),
      );
      const tractorById = new Map<string, NamedRow>();
      const driverById = new Map<string, NamedRow>();
      if (tractorIds.length > 0) {
        const { data, error } = await supabase.from("tractors").select("id, name").in("id", tractorIds);
        if (error) throw error;
        for (const t of (data ?? []) as NamedRow[]) tractorById.set(t.id, t);
      }
      if (driverIds.length > 0) {
        const { data, error } = await supabase.from("drivers").select("id, name").in("id", driverIds);
        if (error) throw error;
        for (const d of (data ?? []) as NamedRow[]) driverById.set(d.id, d);
      }

      const legsByOrder = new Map<string, DuplicateLegRow[]>();
      for (const l of legRows) {
        if (!l.order_id) continue;
        const list = legsByOrder.get(l.order_id) ?? [];
        list.push({
          id: l.id,
          leg_sequence: l.leg_sequence,
          trip_type: l.trip_type,
          ready_time: l.ready_time,
          ready_time_end: l.ready_time_end,
          requested_delivery_time: l.requested_delivery_time,
          requested_delivery_time_end: l.requested_delivery_time_end,
          customer_leg_id: l.customer_leg_id,
          ship_from_label: legLabel(l.ship_from_location_id, l.ship_from_city),
          ship_to_label: legLabel(l.ship_to_location_id, l.ship_to_city),
          tractor_name: l.assigned_tractor_id
            ? tractorById.get(l.assigned_tractor_id)?.name ?? null
            : null,
          operator_names: [l.assigned_operator_id, l.assigned_operator_2_id]
            .map((id) => (id ? driverById.get(id)?.name ?? null : null))
            .filter((n): n is string => Boolean(n)),
        });
        legsByOrder.set(l.order_id, list);
      }
      for (const order of orders) {
        const from = order.ship_from_location_id ? locationById.get(order.ship_from_location_id) : null;
        const to = order.ship_to_location_id ? locationById.get(order.ship_to_location_id) : null;
        byId.set(order.id, {
          ...order,
          ship_from_location: from ? { ...from, name: null } : null,
          ship_to_location: to ? { ...to, name: null } : null,
          billing_party: order.billing_party_id ? customerById.get(order.billing_party_id) ?? null : null,
          assigned_tractor: order.assigned_tractor_id ? tractorById.get(order.assigned_tractor_id) ?? null : null,
          assigned_operator: order.assigned_operator_id ? driverById.get(order.assigned_operator_id) ?? null : null,
          assigned_operator_2: order.assigned_operator_2_id
            ? driverById.get(order.assigned_operator_2_id) ?? null
            : null,
          legs: (legsByOrder.get(order.id) ?? []).sort(
            (x, y) => (x.leg_sequence ?? 0) - (y.leg_sequence ?? 0),
          ),
        });
      }

      return pairs
        .map((p) => {
          const a = byId.get(p.order_a_id);
          const b = byId.get(p.order_b_id);
          if (!a || !b) return null;
          // Prefer API-created on the left, ATMS on the right.
          const leftIsApi = !!a.is_api_created;
          const [left, right] = leftIsApi ? [a, b] : [b, a];
          return { a: left, b: right, matchReason: p.match_reason };
        })
        .filter((x): x is DuplicatePair => x !== null);
    },
  });
}